import { useState, useEffect } from "react";
import { useRegister } from "./RegisterContext";

/* ── Inline SVG icons ──────────────────────────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconWallet   = () => <Icon d={["M21 12V7H5a2 2 0 0 1 0-4h14v4","M3 5v14a2 2 0 0 0 2 2h16v-5","M18 12a2 2 0 0 0 0 4h4v-4Z"]} />;
const IconCash     = () => <Icon d={["M12 1v22","M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"]} />;
const IconCard     = () => <Icon d={["M2 7h20v14H2z","M2 11h20"]} />;
const IconArrowOut = () => <Icon d={["M12 15V3","M7 8l5-5 5 5","M5 21h14"]} />;
const IconTarget   = () => <Icon d={["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z","M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z","M12 12h.01"]} />;
const IconClock    = () => <Icon d={["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z","M12 6v6l4 2"]} />;

const fmt = (n) => `${Number(n || 0).toFixed(3)} TND`;

/**
 * Parses a date string coming from the backend safely.
 *
 * Root cause of the "starts at 1h instead of 0h" bug: when the API
 * serializes a Java LocalDateTime (no timezone info) such as
 * "2026-08-07T14:00:00", the JS Date constructor interprets a
 * timezone-less ISO string as LOCAL browser time — NOT UTC. If the
 * server actually stores/sends that timestamp in UTC, the browser
 * (Tunis = UTC+1) ends up reading it as already being "local", which
 * makes it look 1 hour further in the past than it really is, so the
 * very first render already shows "1h 0m" of elapsed time.
 *
 * Fix: if the string has no explicit timezone offset (no trailing Z
 * and no +HH:MM/-HH:MM), assume it's UTC and append "Z" before
 * parsing, so `now - openingDate` reflects real elapsed time
 * regardless of the browser's local timezone.
 */
function parseServerDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(value);
  return new Date(hasTimezone ? value : `${value}Z`);
}

function formatDuration(openingDate, now) {
  if (!openingDate) return "—";
  const opened = parseServerDate(openingDate);
  const diffMs = Math.max(0, now - opened.getTime());
  const hours = Math.floor(diffMs / 3_600_000);
  const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
  const seconds = Math.floor((diffMs % 60_000) / 1_000);
  return `${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`;
}

/* ── Scoped CSS ─────────────────────────────────────── */
const DASHBOARD_CSS = `
  .rd-dashboard {
    background: linear-gradient(135deg, #111420, #0d0f18);
    border-bottom: 1px solid rgba(255,255,255,0.06);
    padding: 16px 20px;
    display: flex; flex-direction: column; gap: 16px;
  }
  .rd-header {
    display: flex; justify-content: space-between; align-items: flex-start;
  }
  .rd-header-info h3 {
    margin: 0 0 4px 0;
    font-size: 15px; font-weight: 700; color: #fff;
  }
  .rd-meta {
    display: flex; align-items: center; gap: 8px;
    font-size: 12px; color: rgba(255,255,255,0.4);
  }
  .rd-status-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #2ed573;
    box-shadow: 0 0 6px rgba(46,213,115,0.6);
  }
  .rd-dot-sep { color: rgba(255,255,255,0.2); }
  .rd-live-duration {
    font-variant-numeric: tabular-nums;
    color: #8b8ff7;
    font-weight: 600;
  }

  .rd-actions {
    display: flex; gap: 10px;
  }
  .rd-btn-outline {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 16px;
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 8px;
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.6);
    font-size: 12px; font-weight: 500; cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
  }
  .rd-btn-outline:hover {
    border-color: #6366f1; color: #8b8ff7;
    background: rgba(99,102,241,0.08);
  }
  .rd-btn-danger {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 16px;
    border: 1px solid rgba(255,71,87,0.3);
    border-radius: 8px;
    background: rgba(255,71,87,0.08);
    color: #ff6b7a;
    font-size: 12px; font-weight: 500; cursor: pointer;
    transition: background 0.15s, color 0.15s;
  }
  .rd-btn-danger:hover { background: rgba(255,71,87,0.16); color: #fff; }

  .rd-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 12px;
  }
  .rd-card {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex; flex-direction: column; gap: 8px;
    transition: border-color 0.2s;
  }
  .rd-card:hover { border-color: rgba(99,102,241,0.3); }
  .rd-card--warning {
    border-color: rgba(255,168,0,0.3);
    background: rgba(255,168,0,0.06);
  }
  .rd-card--success {
    border-color: rgba(46,213,115,0.2);
    background: rgba(46,213,115,0.04);
  }
  .rd-card__icon {
    color: #8b8ff7;
    display: flex; align-items: center;
  }
  .rd-card__label {
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em;
    color: rgba(255,255,255,0.4); font-weight: 600;
  }
  .rd-card__value {
    font-size: 16px; font-weight: 700; color: #e2e4f0;
  }
  .rd-card--warning .rd-card__value { color: #ffa800; }
  .rd-card--success .rd-card__value { color: #2ed573; }
`;

export default function RegisterDashboard() {
  const { session, setCloseModalVisible, setWithdrawalModalVisible } = useRegister();

  // Ticking clock: re-renders every second so the duration counts up
  // live (h/m/s) instead of being frozen until the next 30s poll.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!session) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [session]);

  const duration = formatDuration(session?.openingDate, now);

  // Inject dashboard styles once
  useEffect(() => {
    const styleId = "register-dashboard-css";
    if (!document.getElementById(styleId)) {
      const el = document.createElement("style");
      el.id = styleId;
      el.textContent = DASHBOARD_CSS;
      document.head.appendChild(el);
    }
  }, []);

  if (!session) return null;

  const openedAt = parseServerDate(session.openingDate);

  const cards = [
    { label: "Opening Balance", value: fmt(session.openingBalance), icon: <IconWallet />, tone: "neutral" },
    { label: "Current Cash",    value: fmt(session.currentCash),    icon: <IconCash />,   tone: session.withdrawalThresholdReached ? "warning" : "neutral" },
    { label: "Cash Sales",      value: fmt(session.cashSalesTotal), icon: <IconCash />,   tone: "success" },
    { label: "Card Sales",      value: fmt(session.cardSalesTotal), icon: <IconCard />,   tone: "neutral" },
    { label: "Withdrawals",     value: fmt(session.withdrawalTotal),icon: <IconArrowOut />, tone: "neutral" },
    { label: "Expected Balance",value: fmt(session.currentCash),    icon: <IconTarget />, tone: "neutral" },
  ];

  return (
    <div className="rd-dashboard">
      <div className="rd-header">
        <div className="rd-header-info">
          <h3>Register Session</h3>
          <div className="rd-meta">
            <span className="rd-status-dot" />
            Open since {openedAt ? openedAt.toLocaleTimeString("fr-TN", { timeZone: "Africa/Tunis" }) : "—"}
            <span className="rd-dot-sep">·</span>
            <IconClock /> <span className="rd-live-duration">{duration}</span>
          </div>
        </div>
        <div className="rd-actions">
          <button className="rd-btn-outline" onClick={() => setWithdrawalModalVisible(true)}>
            <IconArrowOut /> Withdrawal
          </button>
          <button className="rd-btn-danger" onClick={() => setCloseModalVisible(true)}>
            Close Register
          </button>
        </div>
      </div>

      <div className="rd-cards-grid">
        {cards.map(c => (
          <div key={c.label} className={`rd-card${c.tone !== "neutral" ? ` rd-card--${c.tone}` : ""}`}>
            <div className="rd-card__icon">{c.icon}</div>
            <div className="rd-card__label">{c.label}</div>
            <div className="rd-card__value">{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}